//src/components/gro/GroSettingsForm.tsx
import { Save, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { GroSettingsUpdateIn } from "@/types/gro";

type Props = {
  value: GroSettingsUpdateIn;
  readOnly?: boolean;
  saving?: boolean;
  onChange: (next: GroSettingsUpdateIn) => void;
  onSubmit: () => void;
};

export function GroSettingsForm({
  value,
  readOnly = false,
  saving = false,
  onChange,
  onSubmit,
}: Props) {
  function setField<K extends keyof GroSettingsUpdateIn>(
    key: K,
    fieldValue: GroSettingsUpdateIn[K],
  ) {
    onChange({
      ...value,
      [key]: fieldValue,
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Comportamento do módulo GRO
          </CardTitle>
        </CardHeader>

        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Modo de sugestão de risco</Label>
            <Select
              value={value.risk_suggestion_mode}
              onValueChange={(v) =>
                setField("risk_suggestion_mode", v as GroSettingsUpdateIn["risk_suggestion_mode"])
              }
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suggest_only">Apenas sugerir</SelectItem>
                <SelectItem value="auto_approve">Aprovar automaticamente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Modo de sugestão de planos de ação</Label>
            <Select
              value={value.action_plan_suggestion_mode}
              onValueChange={(v) =>
                setField(
                  "action_plan_suggestion_mode",
                  v as GroSettingsUpdateIn["action_plan_suggestion_mode"],
                )
              }
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suggest_only">Apenas sugerir</SelectItem>
                <SelectItem value="auto_approve">Aprovar automaticamente</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Quando automático, os planos sugeridos para o risco são vinculados ao item do inventário assim que ele é criado.
            </p>
          </div>

          <div>
            <Label>Mínimo de respondentes para análise</Label>
            <Input
              type="number"
              min={1}
              max={9999}
              value={value.min_respondents_for_aggregation}
              onChange={(e) =>
                setField(
                  "min_respondents_for_aggregation",
                  Number(e.target.value || 1),
                )
              }
              className="mt-1"
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Recortes analíticos habilitados</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <label className="flex items-start gap-3">
            <Checkbox
              checked={value.allow_unit_analysis}
              onCheckedChange={(checked) =>
                setField("allow_unit_analysis", !!checked)
              }
              disabled={readOnly}
            />
            <div>
              <div className="font-medium text-sm">Análise por unidade</div>
              <div className="text-xs text-muted-foreground">
                Permite consolidar resultados por unidade.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={value.allow_department_analysis}
              onCheckedChange={(checked) =>
                setField("allow_department_analysis", !!checked)
              }
              disabled={readOnly}
            />
            <div>
              <div className="font-medium text-sm">Análise por departamento</div>
              <div className="text-xs text-muted-foreground">
                Permite consolidar resultados por setor/departamento.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={value.allow_job_position_analysis}
              onCheckedChange={(checked) =>
                setField("allow_job_position_analysis", !!checked)
              }
              disabled={readOnly}
            />
            <div>
              <div className="font-medium text-sm">Análise por cargo</div>
              <div className="text-xs text-muted-foreground">
                Permite consolidar resultados por cargo/função.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={value.allow_worker_group_analysis}
              onCheckedChange={(checked) =>
                setField("allow_worker_group_analysis", !!checked)
              }
              disabled={readOnly}
            />
            <div>
              <div className="font-medium text-sm">Análise por grupo de avaliação</div>
              <div className="text-xs text-muted-foreground">
                Permite consolidar resultados por grupo manual ou dinâmico.
              </div>
            </div>
          </label>
        </CardContent>
      </Card>

      {!readOnly ? (
        <div className="flex justify-end">
          <Button onClick={onSubmit} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Salvando..." : "Salvar configurações"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
